/**
 * TeamStrength Hub - Teams Page
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { teamApi } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Users, Plus, ArrowRight, Clock, CheckCircle2, Archive, Trash2
} from 'lucide-react';

const TeamsPage = () => {
  const { currentOrg } = useAuth();
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTeam, setNewTeam] = useState({ name: '', description: '' });

  const fetchTeams = async () => {
    if (!currentOrg) return;
    
    try {
      setLoading(true);
      const response = await teamApi.list(currentOrg.org_id);
      setTeams(response.data);
    } catch (error) {
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeams();
  }, [currentOrg]);

  const handleCreateTeam = async () => {
    if (!newTeam.name.trim()) {
      toast.error('Team name is required');
      return;
    }

    setCreating(true);
    try {
      await teamApi.create(currentOrg.org_id, newTeam);
      toast.success('Team created!');
      setShowCreateDialog(false);
      setNewTeam({ name: '', description: '' });
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create team');
    } finally {
      setCreating(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
      case 'archived':
        return <Archive className="w-4 h-4 text-amber-500" />;
      default:
        return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  if (!currentOrg) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Users className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <p className="text-slate-500 dark:text-slate-400">Select an organization to view teams</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="teams-page">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope' }}>
            Teams
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Manage teams in {currentOrg.name}
          </p>
        </div>
        {(currentOrg.role === 'org_admin') && (
          <Button 
            className="bg-violet-600 hover:bg-violet-700"
            onClick={() => setShowCreateDialog(true)}
            data-testid="create-team-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Team
          </Button>
        )}
      </div>

      {/* Teams Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-40 skeleton rounded-xl" />
          ))}
        </div>
      ) : teams.length === 0 ? (
        <Card className="animate-fade-in">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="w-16 h-16 text-slate-300 dark:text-slate-600 mb-4" />
            <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2" style={{ fontFamily: 'Manrope' }}>
              No teams yet
            </h3>
            <p className="text-slate-500 dark:text-slate-400 text-center max-w-md mb-4">
              Create your first team to start assigning strengths and generating insights.
            </p>
            {(currentOrg.role === 'org_admin') && (
              <Button 
                className="bg-violet-600 hover:bg-violet-700"
                onClick={() => setShowCreateDialog(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Team
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in stagger-1">
          {teams.map((team) => (
            <Card 
              key={team.team_id}
              className="cursor-pointer hover:shadow-md transition-all hover:border-violet-200 dark:hover:border-violet-900 group"
              onClick={() => navigate(`/dashboard/teams/${team.team_id}`)}
              data-testid={`team-card-${team.team_id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(team.status)}
                    <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
                      {team.name}
                    </CardTitle>
                  </div>
                  <Badge 
                    variant={team.status === 'active' ? 'default' : 'secondary'}
                    className="capitalize"
                  >
                    {team.status}
                  </Badge>
                </div>
                {team.description && (
                  <CardDescription className="line-clamp-2 mt-2">
                    {team.description}
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between text-sm text-slate-500">
                  <span>View details</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Team Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Create New Team</DialogTitle>
            <DialogDescription>
              Teams start in draft status. Activate them when ready to use a team slot.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="team-name">Team Name</Label>
              <Input
                id="team-name"
                placeholder="e.g., Marketing Team"
                value={newTeam.name}
                onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                data-testid="team-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="team-description">Description (Optional)</Label>
              <Textarea
                id="team-description"
                placeholder="Brief description of the team..."
                value={newTeam.description}
                onChange={(e) => setNewTeam({ ...newTeam, description: e.target.value })}
                rows={3}
                data-testid="team-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-violet-600 hover:bg-violet-700"
              onClick={handleCreateTeam}
              disabled={creating}
              data-testid="confirm-create-team-btn"
            >
              {creating ? 'Creating...' : 'Create Team'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamsPage;
